import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useMembers } from '../../../context/membersContext.tsx';
import Navbar from '../../NavBar.jsx';
import Loading from '../../Loading.jsx';
import background2 from '../../../images/background2.png';
import gsap from 'gsap';

const MemberPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { members, loading } = useMembers();
  const [member, setMember] = useState(location.state || null);
  const [model, setModel] = useState(false);
  const [tempImgSrc, setTempImgSrc] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [autoPlay, setAutoPlay] = useState(false);

  const autoPlayRef = useRef(null);
  const inactivityRef = useRef(null);
  const heroRef = useRef(null);
  const galleryRef = useRef(null);

  const relatedPhotos = useMemo(() => member?.relatedPhotosURL || [], [member?.relatedPhotosURL]);

  useEffect(() => {
    if (!member) {
      const foundMember = members.find((m) => m.name.split(' ').join('-').toLowerCase() === id);
      setMember(foundMember);
    }
  }, [id, member, members]);

  useEffect(() => {
    if (heroRef.current && member) {
      const ctx = gsap.context(() => {
        gsap.fromTo('.hero-image', { opacity: 0, scale: 0.95 }, { opacity: 1, scale: 1, duration: 0.8, ease: 'power3.out' });
        gsap.fromTo('.hero-content', { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, delay: 0.2, ease: 'power3.out' });
        gsap.fromTo('.hero-bio', { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, delay: 0.4, ease: 'power3.out' });
      }, heroRef);
      return () => ctx.revert();
    }
  }, [member]);

  useEffect(() => {
    if (autoPlay && relatedPhotos.length > 0) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          const nextIndex = (prev + 1) % relatedPhotos.length;
          setTempImgSrc(relatedPhotos[nextIndex]);
          return nextIndex;
        });
      }, 3000);
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [autoPlay, relatedPhotos]);

  const resetInactivityTimer = useCallback(() => {
    setAutoPlay(false);
    if (inactivityRef.current) clearTimeout(inactivityRef.current);
    inactivityRef.current = setTimeout(() => {
      if (model && relatedPhotos.length > 1) setAutoPlay(true);
    }, 5000);
  }, [model, relatedPhotos.length]);

  useEffect(() => {
    if (!model) {
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      setAutoPlay(false);
      return;
    }
    resetInactivityTimer();
    const handleActivity = () => resetInactivityTimer();
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [model, resetInactivityTimer]);

  const handlePrev = useCallback(() => {
    if (relatedPhotos.length === 0) return;
    const newIndex = currentIndex === 0 ? relatedPhotos.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setTempImgSrc(relatedPhotos[newIndex]);
    setZoomLevel(1);
    resetInactivityTimer();
  }, [currentIndex, relatedPhotos, resetInactivityTimer]);

  const handleNext = useCallback(() => {
    if (relatedPhotos.length === 0) return;
    const newIndex = (currentIndex + 1) % relatedPhotos.length;
    setCurrentIndex(newIndex);
    setTempImgSrc(relatedPhotos[newIndex]);
    setZoomLevel(1);
    resetInactivityTimer();
  }, [currentIndex, relatedPhotos, resetInactivityTimer]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
    resetInactivityTimer();
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1));
    resetInactivityTimer();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!model) return;
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') setModel(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [model, handlePrev, handleNext]);

  useEffect(() => {
    if (!model || relatedPhotos.length === 0) return;
    let scrollTimeout;
    const handleWheel = (e) => {
      if (Math.abs(e.deltaX) > 10) {
        e.preventDefault();
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          e.deltaX > 0 ? handleNext() : handlePrev();
        }, 50);
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      clearTimeout(scrollTimeout);
    };
  }, [model, handlePrev, handleNext, relatedPhotos.length]);

  const handleDownload = () => {
    const timestamp = Date.now();
    const filename = `fast-photo-${timestamp}.jpg`;
    const link = document.createElement('a');
    link.href = tempImgSrc;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading || !member) return <Loading />;

  return (
    <>
      <Navbar />

      <section ref={heroRef} className="min-h-screen w-full bg-slate-950 md:pt-32 pt-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="hero-image relative flex items-center justify-center">
              <div className="relative w-full max-w-md mx-auto md:max-w-none">
                <div className="absolute inset-0 bg-mainBlue/20 rounded-3xl blur-3xl scale-110" />
                <img
                  src={member.photoWithoutBackgroundURL || member.photoURL}
                  className="relative w-full h-auto max-h-[70vh] object-contain rounded-2xl"
                  alt={member.name}
                />
              </div>
            </div>

            <div className="hero-content flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-3">
                <p className="text-mainBlue font-bold text-xs uppercase tracking-widest font-custom">{member.role}</p>
                {member.years && (
                  <span className="text-gray-500 text-xs font-medium tracking-wide font-custom">{member.years}</span>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight font-custom">
                {member.name}
              </h1>

              {member.linkedin && (
                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gray-400 hover:text-mainBlue transition-colors mb-6 font-custom text-sm"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn Profile
                </a>
              )}

              <div className="hero-bio bg-white/5 backdrop-blur-sm rounded-2xl p-5 md:p-6 border border-white/10">
                <p className="text-gray-300 text-sm md:text-base leading-relaxed font-custom">
                  {member.description || 'No bio available'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {relatedPhotos.length > 0 && (
        <div ref={galleryRef} className="py-16 md:py-24 px-4 md:px-8 relative bg-cover bg-center" style={{ backgroundImage: `url(${background2})` }}>
          <div className="absolute inset-0 bg-mainBlue/80" />
          <div className="relative z-10 max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-white/60 font-bold text-xs uppercase tracking-widest mb-3 font-custom">Gallery</p>
              <h2 className="font-custom text-3xl md:text-4xl font-bold text-white">Related Photos</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {relatedPhotos.map((photo, index) => (
                <button
                  key={index}
                  className="group relative overflow-hidden rounded-xl aspect-square bg-slate-800"
                  onClick={() => {
                    setTempImgSrc(photo);
                    setCurrentIndex(index);
                    setModel(true);
                  }}
                >
                  <img
                    src={photo}
                    alt={`Related ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {model && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/90 backdrop-blur-sm z-50 p-4"
          onClick={() => setModel(false)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4 text-white">
              <span className="text-sm text-gray-400 font-custom">{currentIndex + 1} / {relatedPhotos.length}</span>
              <div className="flex gap-2 items-center">
                <div className="flex items-center gap-1 mr-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${zoomLevel <= 1 ? 'opacity-30 cursor-not-allowed' : 'bg-white/10 hover:bg-white/20'}`}
                    disabled={zoomLevel <= 1}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM13.5 10.5h-3" />
                    </svg>
                  </button>
                  <span className="text-xs text-gray-400 w-10 text-center">{Math.round(zoomLevel * 100)}%</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${zoomLevel >= 3 ? 'opacity-30 cursor-not-allowed' : 'bg-white/10 hover:bg-white/20'}`}
                    disabled={zoomLevel >= 3}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
                    </svg>
                  </button>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-all"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                </button>
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-all"
                  onClick={() => setModel(false)}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <button
              className="absolute top-1/2 -left-4 md:-left-12 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
              onClick={(e) => { e.stopPropagation(); handlePrev(); }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>

            <div className="flex items-center justify-center min-h-[300px] md:min-h-[400px]">
              <img
                src={tempImgSrc}
                className="max-w-full max-h-[60vh] md:max-h-[65vh] object-contain rounded-xl transition-transform duration-200"
                style={{ transform: `scale(${zoomLevel})`, cursor: zoomLevel < 3 ? 'zoom-in' : 'zoom-out' }}
                alt="Full size"
                onClick={(e) => {
                  e.stopPropagation();
                  if (zoomLevel < 3) handleZoomIn();
                  else setZoomLevel(1);
                }}
              />
            </div>

            <button
              className="absolute top-1/2 -right-4 md:-right-12 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
              onClick={(e) => { e.stopPropagation(); handleNext(); }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>

            <div className="mt-4 overflow-x-auto pb-2">
              <div className="flex gap-2 justify-center">
                {relatedPhotos.map((photo, index) => (
                  <button
                    key={index}
                    className={`w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden transition-all flex-shrink-0 ${
                      index === currentIndex ? 'ring-2 ring-mainBlue scale-105' : 'opacity-40 hover:opacity-80'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(index);
                      setTempImgSrc(photo);
                      setZoomLevel(1);
                      resetInactivityTimer();
                    }}
                  >
                    <img src={photo} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MemberPage;
